import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { moduleForAdminPath, ADMIN_MODULES } from "@/lib/admin-modules";
import { toast } from "sonner";
import { ShieldAlert } from "lucide-react";

/**
 * Guardia de módulo. Lee la sub-ruta actual dentro de /admin y comprueba
 * que el usuario tenga permiso `ver` sobre el módulo mapeado.
 *
 * - super_admin → pasa siempre.
 * - Sin mapeo → bloquea (excepto super_admin).
 * - Sin permiso → 403 inline (data-testid="forbidden-page") + auto-redirect.
 *
 * Iter 200 (P0): Fuente única de verdad ruta→módulo viene de la API
 * `/api/system/route-mapping` (cargado en AuthContext.routeMapping).
 * El mapeo estático en `admin-modules.js` queda como fallback.
 *
 * IMPORTANTE: todos los hooks se llaman SIEMPRE en el mismo orden, antes de
 * cualquier return. Reglas de React Hooks respetadas.
 */
export default function RequireModule({ children }) {
  const { user, permissions, isSuperAdmin, permissionsLoaded, routeMapping } = useAuth();
  const location = useLocation();

  // Sub-ruta dentro de /admin ("", "citas", "catalogos-rrhh/puestos", ...)
  const subPath = location.pathname.replace(/^\/admin\/?/, "").replace(/\/+$/, "");

  // Resolver módulo: 1º mapping dinámico de API, 2º exacto, 3º head/prefix
  let moduleSlug = null;
  if (routeMapping && Object.keys(routeMapping).length > 0) {
    if (subPath in routeMapping) {
      moduleSlug = routeMapping[subPath];
    } else {
      // intentar match por segmentos descendentes (e.g. catalogos-rrhh/puestos → catalogos-rrhh)
      const parts = subPath.split("/");
      while (parts.length > 0) {
        parts.pop();
        const candidate = parts.join("/");
        if (candidate in routeMapping) { moduleSlug = routeMapping[candidate]; break; }
      }
    }
  }
  // Fallback al mapeo estático mientras el dinámico no haya cargado o sea desconocido
  if (!moduleSlug) moduleSlug = moduleForAdminPath(subPath);

  // Estados derivados (sin hooks, son cálculos puros)
  const waitingMapping = !!user && routeMapping === null;
  const waiting = !!user && (!permissionsLoaded || waitingMapping);
  const allowed = !waiting && (
    isSuperAdmin ||
    (moduleSlug != null && Array.isArray(permissions?.[moduleSlug]) &&
      (permissions[moduleSlug].includes("ver") || permissions[moduleSlug].includes("todo")))
  );
  const denied = !waiting && !allowed && !!user;

  // Side-effect: toast cuando se deniega. Único, no se repite por re-render.
  useEffect(() => {
    if (denied) {
      toast.error("No tienes permisos para acceder a este módulo");
    }
  }, [denied, location.pathname]);

  // Side-effect: auto-redirect tras 2.5s para que el usuario lea el 403.
  useEffect(() => {
    if (!denied) return;
    const t = setTimeout(() => {
      if (typeof window !== "undefined" && window.location.pathname !== "/admin") {
        window.location.replace("/admin");
      }
    }, 2500);
    return () => clearTimeout(t);
  }, [denied]);

  // ─── Render ───────────────────────────────────────────────────────
  if (waiting) {
    return (
      <div
        className="flex items-center justify-center min-h-[60vh]"
        data-testid="permissions-loading"
      >
        <div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (denied) {
    return (
      <div
        className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center"
        data-testid="forbidden-page"
      >
        <ShieldAlert className="w-12 h-12 text-red-600 mb-4" />
        <h1 className="heading-industrial text-2xl uppercase text-slate-900 mb-2">
          403 · Acceso denegado
        </h1>
        <p className="text-sm text-slate-600 mb-1">
          No tienes permisos para acceder a este módulo.
        </p>
        {moduleSlug && ADMIN_MODULES[moduleSlug] && (
          <p className="text-xs text-slate-500 mb-4">
            Módulo:{" "}
            <code className="bg-slate-100 px-1 rounded">
              {ADMIN_MODULES[moduleSlug].label}
            </code>
          </p>
        )}
        {!moduleSlug && (
          <p className="text-xs text-amber-700 mb-4">
            Esta ruta no tiene módulo asignado. Pide al super_admin que la añada al mapeo.
          </p>
        )}
        <p className="text-[10px] text-slate-400 mt-3">Volviendo al dashboard...</p>
      </div>
    );
  }

  return children;
}
